/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useField } from 'formik';
import { useDebounce } from 'react-use';

import { AIsuggestion } from 'models/response';
import { Q1Value, FormValues } from 'models/documents';

import actions from 'state/actions';
import selectors from 'state/selectors';
import utils from 'utils';
import intlHelper from 'utils/helpers/intl.helper';

import DataGrid, { DataGridRow } from 'components/DataGrid';
import Header from 'components/DataGrid/Header';
import { RootState } from 'models';
import { useEffectOnce } from 'react-use';
import { actionType } from 'utils/constants';
import Balance from './Balance';
import { BodyContainer } from './style';
import makeGridColums from './Columns';
import { getInitialTextColumnWidths } from './utils';
import MultiTabs from 'components/Tabs/MultiTabs';
import { makeDataPredictionGridColumns } from './PredictionValues/ColumnsPrediction';

interface Props {
  hasRegProtocol?: boolean;
}

const Body = (props: Props) => {
  const { hasRegProtocol } = props;
  const dispatch = useDispatch();
  const nameBody = useSelector(selectors.documents.selectBodyName) || '';
  const headerName = useSelector(selectors.documents.selectHeaderName) || '';
  const userPreference = useSelector(selectors.user.getUserPreference);
  const documentHeader = useSelector(selectors.documents.selectDocumentHeader);
  const readOnly = useSelector(selectors.documents.selectIsTemplateReadOnly);
  const isDocReadOnly = useSelector(selectors.documents.selectIsDocReadOnly);
  const idCompany = useSelector(selectors.documents.selectAllActiveDocuments)[0]?.idCompany || -1;
  const idDocType = documentHeader?.actualDocumentType?.idDocType || 0;

  // body's columns
  const columnsBody = useSelector(selectors.documents.selectBodyColumns);

  // field
  const [field, , helpers] = useField<Q1Value>(nameBody);
  // body's rows
  const [selectedRow, setSelectedRow] = useState<DataGridRow | null>(null);
  // body's filters visibility
  const [hasFilter, setHasFilter] = useState(false);
  // body's filters set
  const [hasFilterSet, setHasFilterSet] = useState(false);
  // suggestions
  const suggestions = useSelector(selectors.documents.selectSuggestions);
  // table's  width
  const [width, setWidth] = useState(100);
  const [minWidth, setMinWidth] = useState(100);
  const t = utils.intl.useTranslator();
  const focusedQ1 = useSelector(selectors.documents.selectFocussedQ1Cell);
  const showBody = useSelector(selectors.documents.selectShowBody);
  const [q1name, setQ1name] = useState(focusedQ1.name);
  const [internalSuggestions] = useState(suggestions);
  const [gridRef, setGridRef] = useState(null);
  const config = useSelector((store: RootState) => selectors.app.getConfig(store, actionType.AUTO_VIEW_IN_PAGE));
  const decimalScale = useSelector(selectors.documents.selectDocumentDecimal);
  const predictionsData = useSelector(selectors.documents.selectFormattedPredictionsData);
  const selectedRowPrediction = useSelector(selectors.documents.setSelectedPredictionRow);
  const predictionsValues = useSelector(selectors.documents.selectPredictionsData);

  useEffect(() => {
    if (focusedQ1.name) setQ1name(focusedQ1.name);
  }, [focusedQ1]);

  // Balance
  // balance field
  const balanceField = useSelector(selectors.documents.selectBalanceField);

  // field value for amount
  const [fieldBalance] = useField<Q1Value>(balanceField || '');
  // amount value
  const [amountValue, setAmountValue] = useState<number>(0);
  // balance value
  const [balanceValue, setBalanceValue] = useState<number>(0);

  // document sign based on movType
  const [documentSign, setDocumentSign] = useState<1 | -1>(1);
  const [fieldMovType] = useField<FormValues['movTypeHeader']>('movTypeHeader');

  useEffect(() => {
    if (fieldMovType.value) {
      const movType = documentHeader?.availableHMoveTypes?.find((e) => e.code === fieldMovType.value?.value);
      movType && setDocumentSign(() => (movType.changeSign ? 1 : -1));
    } else {
      setDocumentSign(1);
    }
  }, [fieldMovType, documentHeader]);

  const fieldBalanceContent = fieldBalance.value?.content;

  useEffect(() => {
    // Set the amount value
    if (balanceField) {
      const field = fieldBalanceContent?.[0];
      if (field) {
        const amountNotParsed = typeof field === 'string' ? field : field.content;
        const amount = intlHelper.convertToNumber(amountNotParsed);
        setAmountValue(amount);
      }
    }
  }, [fieldBalanceContent, balanceField]);

  useEffect(() => {
    try {
      // Calculation balance
      if (balanceField && amountValue && columnsBody && documentSign) {
        const values = field.value.body?.map((row) => {
          // Find the column with identifier -2
          // the one that identifies the sign of the movement
          const columnSign = columnsBody.find(({ code }) => code === -2);
          // if this column is not present, a configuration error has been committed
          // and the calculation is not possible
          if (!columnSign) throw new Error('Obbligatory Sign Column with code -2 not found');
          // determine the sign of the row
          const rowSign = columnSign.hTableValues.find(({ code }) => code === row['-2'].content)?.sign;
          // if it is not possible to determine the sign of the row, a configuration error has been committed
          // and the calculation is not possible
          if (!rowSign) throw new Error('Row Sign Column not found');
          const finalSign = documentSign * rowSign;
          // The amount of the row is determined by cells that correspond to columns with code -1
          const rowImport = utils.intl.convertToNumber(row['-1']?.content || '0');
          return finalSign * rowImport;
        });
        const balance = values?.reduce((a, b) => a + b, 0) || 0;
        const sum = parseFloat((amountValue + balance).toFixed(decimalScale));
        const balanceValue = intlHelper.formatNumber(sum, decimalScale, ',');
        setBalanceValue(intlHelper.convertToNumber(balanceValue));
      }
    } catch (e) {
      console.warn(e);
    }
  }, [field.value.body, amountValue, documentSign, balanceField, columnsBody, decimalScale]);

  useEffect(() => {
    // update body fields when decimal is changed
    if (field) {
      const newBody = field.value.body?.map((elem) => {
        const formattedElem = { ...elem };

        Object.keys(elem).forEach((rowCode) => {
          const currencyCell = columnsBody?.find(
            ({ code, type, dataType }) => code.toString() === rowCode && type === 3 && dataType === 0,
          );
          if (currencyCell) {
            formattedElem[rowCode] = {
              ...elem[rowCode],
              content: elem[rowCode].content
                ? intlHelper.formatNumber(intlHelper.convertToNumber(elem[rowCode].content), decimalScale, ',')
                : '',
            };
          }
        });

        return formattedElem;
      });

      if (newBody) {
        helpers.setValue({
          ...field.value,
          body: newBody,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decimalScale, columnsBody]);

  const formatData = React.useCallback(() => {
    return field.value.body?.map((el) => {
      const result: DataGridRow = { id: '0' };
      for (const [key, value] of Object.entries(el)) {
        if (key === 'id') {
          result.id = value;
        } else {
          result[key] = value.content;
        }
      }
      return result;
    });
  }, [field.value.body]);

  const getFormattedData = React.useMemo(() => formatData(), [formatData]);

  // State for calculated widths of text columns
  // Calculate text column widths on first render
  const [initialTextWidths, setInitialTextWidths] = React.useState<{ [key: string]: number }>(() => {
    return getInitialTextColumnWidths(columnsBody || [], getFormattedData || []);
  });
  // State to track which text columns have been manually resized
  // State to keep track of previous column widths (useRef to avoid re-render)
  const prevWidths = React.useRef<{ [key: string]: number }>({ ...initialTextWidths });
  const [userResizedTextColumns, setUserResizedTextColumns] = React.useState<{ [key: string]: boolean }>({});
  // State to keep track of previous widths

  // Update widths only for text columns not manually resized
  // Update text column widths only for those not manually resized
  useDebounce(
    () => {
      const newWidths = getInitialTextColumnWidths(columnsBody || [], getFormattedData || []);
      setInitialTextWidths((prev) => {
        // If prev is empty (first render), use newWidths directly
        if (Object.keys(prev).length === 0) {
          return newWidths;
        }
        // Otherwise, update only columns not manually resized
        const updated = { ...prev };
        Object.entries(newWidths).forEach(([key, value]) => {
          if (!userResizedTextColumns[key]) {
            updated[key] = value;
          }
        });
        return updated;
      });
    },
    300,
    [columnsBody, getFormattedData, userResizedTextColumns],
  );

  // Resize handler: updates only the actually resized column

  const columns = React.useMemo(
    () =>
      makeGridColums(
        columnsBody || [],
        nameBody,
        userPreference,
        getFormattedData || [],
        idCompany,
        idDocType,
        gridRef,
        initialTextWidths,
        readOnly,
      ),
    [
      columnsBody,
      nameBody,
      userPreference,
      getFormattedData,
      gridRef,
      initialTextWidths,
      readOnly,
      idCompany,
      idDocType,
    ],
  );

  const onSelection = (row: DataGridRow | null, columnId?: number) => {
    setSelectedRow(row);
    // reset focussed q1 state
    dispatch(actions.documents.setFocussedQ1({ fieldInputType: 0, name: '' }));
    if (row && columnId) {
      const type = columnsBody?.find(({ code }) => code === columnId)?.type || 0;
      dispatch(actions.documents.setQ3Cell({ rowId: Number(row.id), columnId, type }));
    }
  };

  const onToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    helpers.setValue({
      ...field.value,
      content: [
        {
          ...field.value.content[0],
          status: e.target.checked ? 3 : 0,
        },
      ],
    });
  };

  const createNextIdRow = () => {
    if (field.value.body && field.value.body.length) {
      const rowIds = field.value.body.map((row) => {
        return Number(row.id);
      });
      const highestId = Math.max.apply(null, rowIds);
      const highest = highestId + 1;
      return highest.toString();
    } else {
      return '0';
    }
  };

  const addRow = () => {
    const newEmptyObjRow = {
      id: createNextIdRow(),
    };
    if (field.value.body) {
      if (balanceField) {
        const importNewRow = () => balanceValue;
        const defaultValue = () => {
          if (documentSign === 1) {
            return '50';
          } else {
            return '40';
          }
        };
        const newObj = {
          id: createNextIdRow(),
          '-2': {
            content: defaultValue(),
            pageNumber: -1,
            boundingBox: {
              x1: -1,
              y1: -1,
              x2: -1,
              y2: -1,
            },
          },
          '-1': {
            content: utils.intl.formatNumber(importNewRow(), decimalScale, ','),
            pageNumber: -1,
            boundingBox: {
              x1: -1,
              y1: -1,
              x2: -1,
              y2: -1,
            },
          },
        };
        helpers.setValue({ ...field.value, body: [...field.value.body, newObj] });
      } else {
        helpers.setValue({ ...field.value, body: [...field.value.body, newEmptyObjRow] });
      }
    }
  };

  const removeRow = () => {
    if (selectedRow && field.value.body) {
      const body = field.value.body.filter((item) => item.id !== selectedRow?.id);
      helpers.setValue({ ...field.value, body });
    } else {
      return null;
    }
  };

  const copyRow = () => {
    if (selectedRow && field.value.body) {
      const id = Number(selectedRow.id);
      const duplicatedRow = { ...field.value.body[id], id: createNextIdRow() };
      helpers.setValue({ ...field.value, body: [...field.value.body, duplicatedRow] });
    }
  };

  // Helper function for handling currency formatting
  const handleCurrencyFormatting = (value: string, decimalSeparator: string) => {
    if (decimalSeparator === ',') {
      value = value.replace(/\./g, ''); // Remove dots
    } else if (decimalSeparator === '.') {
      value = value.replace(/,/g, ''); // Remove commas
    }
    return value; // Return formatted value
  };

  const pasteRow = async () => {
    try {
      const text = await navigator.clipboard.readText();

      const rows = text
        .trim()
        .split('\n')
        .filter((row) => row.trim() !== '');

      const existingIds = new Set((field.value.body || []).map((row) => row.id));
      const newRows = [...(field.value.body || [])];

      rows.forEach((row) => {
        const values = row.trim().split('\t');

        const newObj: DataGridRow = {
          id: selectedRow ? selectedRow.id : `${newRows.length}`,
        };

        const dynamicFields: { [key: string]: any } = {};

        columnsBody?.forEach((col, colIndex) => {
          const columnKey = col.code;

          // Skip disabled columns
          if (col.dataType === 0 && col.type === 21) {
            return;
          }

          if (columnKey) {
            let contentValue: any = values[colIndex] || '';

            // Handle both date and currency in one condition
            if (col.dataType === 0) {
              if (col.type === 1) {
                // Date
                if (!utils.date.matchesUserDateFormat(contentValue)) {
                  contentValue = null; // Skip invalid date format
                } else {
                  const newDate = utils.date.convertDateStringToISO(contentValue);
                  if (newDate) {
                    contentValue = utils.date.formatDateForDb(newDate);
                  }
                }
              } else if (col.type === 3) {
                // Currency
                contentValue = handleCurrencyFormatting(contentValue, userPreference.decimalSep);
              } else if (col.type === 4) {
                // Boolean
                // Convert to 'true' or 'false', or set to null if not valid
                const lowerCaseValue = contentValue.toString().toLowerCase();
                if (lowerCaseValue === 'true') {
                  contentValue = 'true';
                } else if (lowerCaseValue === 'false') {
                  contentValue = 'false';
                } else {
                  contentValue = null; // Set to null if not 'true' or 'false'
                }
              }
            }

            // Add dynamic field
            dynamicFields[columnKey] = {
              content: contentValue,
              pageNumber: -1,
              boundingBox: {
                x1: -1,
                y1: -1,
                x2: -1,
                y2: -1,
              },
            };
          }
        });

        // Merge dynamic fields into the new object
        Object.assign(newObj, dynamicFields);

        // Update or add new row to newRows
        if (selectedRow) {
          const rowIndex = newRows.findIndex((r) => r.id === selectedRow.id);
          if (rowIndex !== -1) {
            newRows[rowIndex] = newObj; // Replaces the existing row
          }
        } else if (newObj.id && !existingIds.has(newObj.id)) {
          newRows.push(newObj);
          existingIds.add(newObj.id);
        }
      });

      // Update the field value with new rows
      helpers.setValue({ ...field.value, body: newRows });
    } catch (error) {
      console.error(t('error during paste'), error);
    }
  };

  const removeRows = () => {
    helpers.setValue({ ...field.value, body: [] });
  };

  const onFilterToggle = () => setHasFilter(!hasFilter);

  const closeTableFields = () => {
    try {
      const q1 = document.querySelector<HTMLInputElement>(`input[name='${q1name}']`);
      if (q1) q1.focus();
    } catch (e) {
      console.error(e);
    }
    dispatch(actions.documents.setShowBody(false));
    dispatch(actions.documents.setActiveBox(null));
  };

  const downloadCsv = () => {
    try {
      const header = columns?.map(({ title, id }) => ({ accessor: `${id}`, label: title })) || [];

      const columnsTypeDateIDs = columns?.filter(({ type }) => type === 'date')?.map(({ id }) => `${id}`) || [];

      const body =
        field.value?.body?.map((row) => {
          const enhancedRow: { [key: string]: string } = {};
          const rowIDs = Object.keys(row);
          rowIDs.forEach((rowID) => {
            if (columnsTypeDateIDs.includes(rowID)) {
              enhancedRow[rowID] = utils.date.convertDate(row[rowID].content, userPreference.timeZone ?? 'UTC') || '';
            } else {
              enhancedRow[rowID] = row[rowID].content || '';
            }
          });
          return enhancedRow;
        }) || [];

      const csv = utils.file.convertToCSV(header, body);
      utils.file.downloadCsv(headerName, csv);
    } catch (e) {
      console.error(e);
      utils.app.notify('fail', e as Error);
    }
  };

  const increaseWidth = () => {
    setWidth(width + 10);
  };

  const decreaseWidth = () => {
    setWidth(width - 10);
  };

  const setFieldVal = (newVal: Q1Value) => {
    helpers.setValue(newVal);
  };

  useEffect(() => {
    const calcTableWidth = (columns: number) => {
      if (columns > 5) return columns * 20;
      return 100;
    };
    if (columns?.length) {
      const width = calcTableWidth(columns.length);
      setWidth(width);
      setMinWidth(width);
    }
  }, [columns]);

  const formatPredictionsData = React.useCallback(() => {
    if (!predictionsValues) {
      return [];
    }

    return predictionsValues.map((row) => {
      const result: DataGridRow = { id: '0' };

      for (const [key, columnData] of Object.entries(row)) {
        const column = columnData as {
          predictionContentList: { content: string; confidence: number }[];
          rowNumber: number;
        };

        // Set the row ID
        result.id = String(column.rowNumber);
        const columnInfo = columns.find((col) => col.id === parseInt(key));
        const typeOfColumn = columnInfo?.type;

        if (key !== 'rowNumber') {
          const predictionContentList = column.predictionContentList;

          // Check that predictionContentList exists and is an array
          if (Array.isArray(predictionContentList) && predictionContentList.length > 0) {
            if (predictionContentList.length > 1) {
              // Find the element with the highest confidence
              const highestConfidencePrediction: any = predictionContentList.reduce((prev, curr) => {
                return prev.confidence > curr.confidence ? prev : curr;
              });

              let formattedContent = highestConfidencePrediction.content;

              if (typeOfColumn === 'date' && formattedContent) {
                formattedContent = utils.date.convertSelectedDateFromPrediction(formattedContent) || ''; // Convert date format if necessary
              }

              if (typeOfColumn === 'number') {
                formattedContent = intlHelper.formatNumber(
                  parseInt(formattedContent),
                  decimalScale,
                  userPreference.decimalSep,
                );
              }
              // Assign the object with `content` and `confidence`
              result[key] = {
                content: formattedContent,
                confidence: highestConfidencePrediction.confidence,
              };
            } else {
              // If there is only one element in the list
              let singlePrediction = predictionContentList[0].content;
              const confidence = predictionContentList[0].confidence;

              if (typeOfColumn === 'date') {
                singlePrediction = utils.date.convertSelectedDateFromPrediction(singlePrediction) || '';
              }

              if (typeOfColumn === 'number') {
                singlePrediction = intlHelper.formatNumber(
                  parseInt(singlePrediction),
                  decimalScale,
                  userPreference.decimalSep,
                );
              }

              result[key] = {
                content: singlePrediction,
                confidence: confidence,
              };
            }
          } else {
            // Handle the case where `predictionContentList` is not a valid array or is empty
            result[key] = {
              content: '',
              confidence: 0, // Assign 0 or a default value for `confidence`
            };
          }
        }
      }

      return result; // Return the formatted row
    });
  }, [predictionsValues, columns, userPreference.decimalSep, decimalScale]);

  const setBestSuggestion = () => {
    const suggestionMaxConfidence = [...internalSuggestions].sort(
      (first: AIsuggestion, last: AIsuggestion) => last.confidence - first.confidence,
    );

    internalSuggestions.length > 0 &&
      dispatch(
        actions.documents.setActiveBox({
          ...suggestionMaxConfidence[0].boundingBox,
          pageNumber: suggestionMaxConfidence[0].pageNumber,
        }),
      );
  };

  useEffectOnce(() => {
    internalSuggestions.length > 0 && config.val === 1 && setBestSuggestion();
  });

  const onCellMove = ({ x, y }: { x: number; y: number }) => {
    if ((x === columns.length && y + 1 === getFormattedData?.length) || (x === -1 && y === 0)) closeTableFields();
  };

  useEffect(() => {
    const data = formatPredictionsData();
    dispatch(actions.documents.setFormattedPredictionsData(data));
  }, [formatPredictionsData, dispatch]);

  const addPredictionToField = () => {
    // Check if selectedRowPrediction exists and is valid
    if (!selectedRowPrediction || typeof selectedRowPrediction !== 'object') return;

    const importNewRow = () => balanceValue;

    // Create a new object with a unique ID
    const newItem = {
      id: (field.value.body?.length || 0).toString(), // Set a unique ID based on the current length of body
      ...Object.fromEntries(
        Object.entries(selectedRowPrediction)
          .filter(([key]) => key !== 'id') // Exclude the 'id' field
          .map(([key, value]) => [
            key,
            {
              ...value, // Include all other properties
              pageNumber: -1, // Add pageNumber to the prediction
              boundingBox: {
                x1: -1,
                y1: -1,
                x2: -1,
                y2: -1,
              }, // Add default boundingBox
            },
          ]),
      ),
      '-1': {
        content: utils.intl.formatNumber(importNewRow(), decimalScale, ','),
        pageNumber: -1,
        boundingBox: {
          x1: -1,
          y1: -1,
          x2: -1,
          y2: -1,
        },
      },
    };

    // Add the new object to the existing array
    const updatedBody = [...(field.value.body || []), newItem];

    // Update the field value with the modified body
    helpers.setValue({
      ...field.value,
      body: updatedBody,
    });
  };

  const columnsPrediction = React.useMemo(
    () =>
      makeDataPredictionGridColumns(
        columnsBody || [],
        predictionsValues || [],
        predictionsData || [],
        userPreference,
        decimalScale,
        predictionsData,
        gridRef,
      ),
    [columnsBody, userPreference, predictionsValues, predictionsData, gridRef, decimalScale],
  );

  const dataGrid = (
    <DataGrid
      hasSorting
      id={nameBody}
      hasFilter={hasFilter}
      disabled={Boolean(readOnly) || Boolean(hasRegProtocol) || Boolean(isDocReadOnly) || field.value.fieldType === 21}
      onSelection={onSelection}
      columns={columns || []}
      rows={getFormattedData || []}
      headerHeight={32}
      rowHeight={38}
      isColumnsResizable={true}
      onFiltersSet={setHasFilterSet}
      field={field.value}
      setFiledVal={setFieldVal}
      onCellMove={onCellMove}
      setGridRef={setGridRef}
      onColumnResize={(widths) => {
        Object.keys(widths).forEach((colId) => {
          const col = columnsBody?.find((c) => String(c.code) === String(colId));
          // Just text columns
          if (col && col.dataType === 0 && col.type === 0) {
            // Compare previous width with the new one
            if (prevWidths.current[colId] !== undefined && prevWidths.current[colId] !== widths[colId]) {
              setUserResizedTextColumns((userPrev) => ({ ...userPrev, [colId]: true }));
            }
          }
          // Update previous widths state
          prevWidths.current[colId] = widths[colId];
        });
      }}
    />
  );

  const dataGridPrediction = (
    <DataGrid
      hasSorting
      id={`${nameBody}_predictions`}
      hasFilter={hasFilter}
      onSelection={(row: DataGridRow | null) => dispatch(actions.documents.setSelectedPredictionsRow(row))}
      columns={columnsPrediction || []}
      rows={predictionsData || []}
      headerHeight={32}
      rowHeight={30}
      isColumnsResizable={true}
      onFiltersSet={setHasFilterSet}
      field={field.value}
      setFiledVal={setFieldVal}
      onCellMove={onCellMove}
      setGridRef={setGridRef}
    />
  );

  const tabs = [
    {
      name: t('actual_coding'),
      content: dataGrid,
      id: 0,
    },
    {
      name: t('ai_prediction'),
      content: dataGridPrediction,
      id: 1,
    },
  ];

  const multiTab = (
    <MultiTabs tabs={tabs} tabWidth="100" singleTabWidth="100" defaultTab={getFormattedData?.length ? 0 : 1} />
  );

  const content = predictionsValues?.length ? multiTab : dataGrid;

  return columnsBody && showBody ? (
    <BodyContainer tableWidth={width}>
      <Header
        minWidth={minWidth}
        width={width}
        increaseWidth={increaseWidth}
        decreaseWidth={decreaseWidth}
        downloadCsv={downloadCsv}
        disabled={
          Boolean(readOnly) || Boolean(hasRegProtocol) || Boolean(isDocReadOnly) || field.value.fieldType === 21
        }
        selectedRow={Boolean(selectedRow)}
        headerTitle={headerName}
        hasHeaderToggle={true}
        headerToggleStatus={field.value.content[0].status === 3}
        hasDefaultButtons
        addRowAction={addRow}
        removeRowAction={removeRow}
        removeRowsAction={removeRows}
        copyRowAction={copyRow}
        pasteRowAction={pasteRow}
        headerToggleFunction={onToggle}
        hasFilterToggle
        filterToggleAction={onFilterToggle}
        closeTableFields={closeTableFields}
        hasFilterSet={hasFilterSet}
        buttonInTitleLabel={t('view in page')}
        buttonInTitleStatus={Boolean(internalSuggestions.length > 0)}
        buttonInTitleAction={setBestSuggestion}
        isPredictionAvailable={!!predictionsValues?.length || false}
        selectedRowPrediction={selectedRowPrediction || undefined}
        addPredictionToField={addPredictionToField}
      />
      {content}
      {balanceField ? <Balance amountValue={amountValue} balanceValue={balanceValue} /> : null}
    </BodyContainer>
  ) : null;
};

export default Body;
